import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import { createContact } from "./contactService";

export interface Client {
  id: string;
  org_id: string;
  name: string;
  abn: string | null;
  /** Alternative company identifier for entities with no ABN. */
  acn: string | null;
  channel_partner: string | null;
  sf_account_id: string | null;
  extn: Record<string, any>;
  created_at: string;
  bill_val: 'active' | 'inactive';
  broker: string | null;
  shield_platinum: boolean;
}

export interface ClientFormData {
  name: string;
  contact_name?: string;
  abn?: string;
  acn?: string;
  channel_partner?: string;
  sf_account_id?: string;
  email?: string;
  phone?: string;
  industry?: string;
  broker?: string;
  shield_platinum?: boolean;
}

/**
 * Get user's organization ID
 */
export const getUserOrganizationId = async (): Promise<string | null> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) return null;

    const { data, error } = await supabase
      .from("base_organisation_members")
      .select("organisation_id")
      .eq("user_id", session.user.id)
      .limit(1)
      .single();

    if (error) {
      console.error("Error getting org ID:", error);
      return null;
    }

    return data?.organisation_id || null;
  } catch (error) {
    console.error("Error in getUserOrganizationId:", error);
    return null;
  }
};

/**
 * Get all clients for the user's organization
 */
export const getClients = async (): Promise<{
  success: boolean;
  data?: Client[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const allData: Client[] = [];
    let page = 0;
    const PAGE_SIZE = 1000;
    while (true) {
      const { data, error } = await supabase
        .from("client")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false })
        // tie-break on the pk: created_at alone is not unique (a bulk import
        // gives thousands of rows the same timestamp), and range paging over a
        // non-deterministic order silently duplicates and skips rows
        .order("id")
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (error) throw error;
      allData.push(...(data || []));
      if (!data || data.length < PAGE_SIZE) break;
      page++;
    }

    return { success: true, data: allData };
  } catch (error: any) {
    console.error("Error fetching clients:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single client by ID
 */
export const getClientById = async (
  id: string
): Promise<{
  success: boolean;
  data?: Client;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from("client")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error fetching client:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new client
 */
export const createClient = async (
  formData: ClientFormData
): Promise<{
  success: boolean;
  data?: Client;
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const clientPayload = {
      org_id: orgId,
      name: formData.name,
      abn: formData.abn || null,
      acn: formData.acn || null,
      channel_partner: formData.channel_partner || null,
      sf_account_id: formData.sf_account_id || null,
      broker: formData.broker || null,
      shield_platinum: formData.shield_platinum ?? false,
      extn: {
        email: formData.email || null,
        phone: formData.phone || null,
        industry: formData.industry || null,
      },
      created_at: new Date().toISOString(),
    };

    const { data: clientData, error: clientError } = await supabase
      .from("client")
      .insert(clientPayload)
      .select()
      .single();

    if (clientError) throw clientError;

    // If contact_name is provided, create a contact linked to this client
    if (formData.contact_name && formData.contact_name.trim()) {
      const contactName = formData.contact_name.trim();
      const nameParts = contactName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const contactResult = await createContact({
        client_id: clientData.id,
        first_name: firstName,
        last_name: lastName,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
      });

      if (!contactResult.success) {
        console.warn("Client created but contact creation failed:", contactResult.error);
        // Don't fail the entire operation if contact creation fails
      }
    }

    return { success: true, data: clientData };
  } catch (error: any) {
    console.error("Error creating client:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing client
 */
export const updateClient = async (
  id: string,
  formData: Partial<ClientFormData>
): Promise<{
  success: boolean;
  data?: Client;
  error?: string;
}> => {
  try {
    // Get current client to merge extn data
    const { data: currentClient, error: fetchError } = await supabase
      .from("client")
      .select("extn")
      .eq("id", id)
      .single();

    if (fetchError) throw fetchError;

    const currentExtn = (currentClient?.extn as Record<string, any>) || {};
    
    const updateData: any = {};

    if (formData.name !== undefined) updateData.name = formData.name;
    if (formData.abn !== undefined) updateData.abn = formData.abn || null;
    if (formData.acn !== undefined) updateData.acn = formData.acn || null;
    if (formData.channel_partner !== undefined)
      updateData.channel_partner = formData.channel_partner || null;
    if (formData.sf_account_id !== undefined)
      updateData.sf_account_id = formData.sf_account_id || null;
    if (formData.broker !== undefined) updateData.broker = formData.broker || null;
    if (formData.shield_platinum !== undefined)
      updateData.shield_platinum = formData.shield_platinum;

    // Update extn with new values
    updateData.extn = {
      ...currentExtn,
      ...(formData.email !== undefined && { email: formData.email }),
      ...(formData.phone !== undefined && { phone: formData.phone }),
      ...(formData.industry !== undefined && { industry: formData.industry }),
    };

    const { data, error } = await supabase
      .from("client")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error updating client:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a client
 */
export const deleteClient = async (
  id: string
): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase.from("client").delete().eq("id", id);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error("Error deleting client:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update bill_val field for a client
 */
export const updateClientBillVal = async (
  id: string,
  billVal: 'active' | 'inactive'
): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase
      .from("client")
      .update({ bill_val: billVal })
      .eq("id", id);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error("Error updating client bill_val:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get client with related data (contacts, sites)
 */
export const getClientWithRelations = async (
  id: string
): Promise<{
  success: boolean;
  data?: {
    client: Client;
    contacts: any[];
    sites: any[];
  };
  error?: string;
}> => {
  try {
    const { data: client, error: clientError } = await supabase
      .from("client")
      .select("*")
      .eq("id", id)
      .single();

    if (clientError) throw clientError;

    const { data: contacts, error: contactsError } = await supabase
      .from("contact")
      .select("*")
      .eq("client_id", id);

    const { data: sites, error: sitesError } = await supabase
      .from("site")
      .select("*")
      .eq("client_id", id);

    return {
      success: true,
      data: {
        client,
        contacts: contacts || [],
        sites: sites || [],
      },
    };
  } catch (error: any) {
    console.error("Error fetching client with relations:", error);
    return { success: false, error: error.message };
  }
};

